import { memo } from 'react';

import styled from 'styled-components';

import { SectionContainer } from 'components/DetailsView/Section';

import type { ZoneSiteRow } from '../constants/zoneSitesColumns';

type TourOrderSectionProps = {
    // Already returned in the automatic tour order by the server (`DeliveryZone.sites`).
    sites: ZoneSiteRow[];
};

// Read-only view of the tour order: it is computed automatically from the sites' GPS positions, so there
// is nothing to configure here — the list just lets the planner see the sequence a deliverer will follow.
export const TourOrderSection = memo(({ sites }: TourOrderSectionProps) => (
    <SectionContainer title="Ordre de tournée (automatique)" isInitiallyOpen>
        {sites.length === 0 ? (
            <EmptyOrder>Ajoutez des sites à la zone pour calculer l'ordre de tournée.</EmptyOrder>
        ) : (
            <>
                <OrderHint>Ordre calculé automatiquement à partir des positions GPS des sites.</OrderHint>
                <OrderList>
                    {sites.map((site, index) => (
                        <OrderRow key={site._id}>
                            <OrderBadge>{index + 1}</OrderBadge>
                            <OrderInfo>
                                <OrderSite title={site.name}>{site.name}</OrderSite>
                                <OrderOrg title={site.organizationName ?? undefined}>{site.organizationName}</OrderOrg>
                            </OrderInfo>
                        </OrderRow>
                    ))}
                </OrderList>
            </>
        )}
    </SectionContainer>
));
TourOrderSection.displayName = 'TourOrderSection';

const EmptyOrder = styled.div`
    padding: 16px;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;

const OrderHint = styled.span`
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const OrderList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: min(70vh, 720px);
    overflow-y: auto;
`;

const OrderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 6px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.infoTextColor};
`;

const OrderBadge = styled.span`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: ${({ theme }) => theme.ctaPrimaryColor};
    flex-shrink: 0;
`;

const OrderInfo = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
    flex: 1;
`;

const OrderSite = styled.span`
    font-weight: 600;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
    max-width: 55%;
`;

const OrderOrg = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
